import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, Package, Star, Award, ChevronDown, Save, Check } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/utils/adminApi';
import { formatCOP } from '@/utils/format';

const ProductRow = ({ product, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [variants, setVariants] = useState(product.variants || []);
  const [featured, setFeatured] = useState(!!product.featured);
  const [bestseller, setBestseller] = useState(!!product.bestseller);
  const [status, setStatus] = useState(product.status || 'active');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const updateVariant = (variantId, field, value) => {
    setVariants((prev) =>
      prev.map((v) => (v.variant_id === variantId ? { ...v, [field]: value } : v))
    );
  };

  const totalStock = variants.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0);
  const minPrice = Math.min(...variants.map((v) => v.price_retail_with_tax || 0));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        status,
        featured,
        bestseller,
        variants: variants.map((v) => ({
          variant_id: v.variant_id,
          stock: parseInt(v.stock) || 0,
          price_retail_with_tax: parseFloat(v.price_retail_with_tax) || 0,
          is_available: v.is_available !== false,
        })),
      };
      await adminAPI.updateProduct(product.id, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E7E5E4] overflow-hidden" data-testid={`admin-product-${product.slug}`}>
      <div
        onClick={() => setOpen(!open)}
        className="p-4 flex items-center gap-4 cursor-pointer hover:bg-[#FAF9F5]"
      >
        <div className="w-14 h-14 rounded-xl bg-[#F7F2E8] overflow-hidden shrink-0 flex items-center justify-center">
          {product.images?.[0] ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${product.images[0]}?size=200`} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <Package className="w-6 h-6 text-[#8BA58F]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[#1C1917] truncate">{product.name}</p>
          <p className="text-xs text-[#57534E]">
            {product.brand} • {product.species} • {variants.length} {variants.length === 1 ? 'variante' : 'variantes'}
          </p>
        </div>
        <div className="hidden md:flex flex-col items-end">
          <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Stock total</p>
          <p className={`font-display font-bold text-lg ${totalStock === 0 ? 'text-red-600' : totalStock < 20 ? 'text-orange-600' : 'text-[#1C1917]'}`}>{totalStock}</p>
        </div>
        <div className="hidden md:flex flex-col items-end">
          <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Desde</p>
          <p className="font-display font-bold text-[#1C1917]">{formatCOP(minPrice)}</p>
        </div>
        <div className="flex items-center gap-1">
          {featured && <Star className="w-4 h-4 text-[#D47A4A] fill-[#D47A4A]" />}
          {bestseller && <Award className="w-4 h-4 text-[#D47A4A]" />}
          {status === 'inactive' && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-red-50 text-red-700 rounded-full font-medium">Inactivo</span>
          )}
          <ChevronDown className={`w-5 h-5 text-[#8BA58F] transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {open && (
        <div className="border-t border-[#F5F5F4] p-4 bg-[#FAF9F5]" data-testid={`admin-product-editor-${product.slug}`}>
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={status === 'active'}
                onChange={(e) => setStatus(e.target.checked ? 'active' : 'inactive')}
                data-testid={`toggle-active-${product.slug}`}
                className="w-4 h-4"
              />
              Activo
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                data-testid={`toggle-featured-${product.slug}`}
                className="w-4 h-4"
              />
              Destacado
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={bestseller}
                onChange={(e) => setBestseller(e.target.checked)}
                data-testid={`toggle-bestseller-${product.slug}`}
                className="w-4 h-4"
              />
              Más vendido
            </label>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-[#8BA58F]">
                  <th className="text-left py-2 pr-3">SKU / Presentación</th>
                  <th className="text-right py-2 pr-3 w-32">Precio (COP)</th>
                  <th className="text-right py-2 pr-3 w-24">Stock</th>
                  <th className="text-center py-2 w-20">Disponible</th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => (
                  <tr key={v.variant_id} className="border-t border-[#E7E5E4]">
                    <td className="py-2 pr-3">
                      <p className="font-medium text-[#1C1917]">{v.size}</p>
                      <p className="text-xs text-[#57534E]">SKU {v.sku}</p>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <input
                        type="number"
                        value={v.price_retail_with_tax}
                        onChange={(e) => updateVariant(v.variant_id, 'price_retail_with_tax', e.target.value)}
                        className="w-full text-right px-2 py-1.5 rounded-lg border border-[#E7E5E4] focus:outline-none focus:border-[#2A5C43]"
                        data-testid={`variant-price-${v.sku}`}
                      />
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <input
                        type="number"
                        value={v.stock ?? 0}
                        onChange={(e) => updateVariant(v.variant_id, 'stock', e.target.value)}
                        className="w-full text-right px-2 py-1.5 rounded-lg border border-[#E7E5E4] focus:outline-none focus:border-[#2A5C43]"
                        data-testid={`variant-stock-${v.sku}`}
                      />
                    </td>
                    <td className="py-2 text-center">
                      <input
                        type="checkbox"
                        checked={v.is_available !== false}
                        onChange={(e) => updateVariant(v.variant_id, 'is_available', e.target.checked)}
                        data-testid={`variant-available-${v.sku}`}
                        className="w-4 h-4"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end mt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6"
              data-testid={`save-product-${product.slug}`}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : saved ? <Check className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Guardando...' : saved ? 'Guardado' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listProducts({ search, limit: 200 });
      setProducts(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetch, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetch, search]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-products-title">Inventario</h1>
          <p className="text-[#57534E] mt-2">{products.length} productos • Edita precios, stock y visibilidad</p>
        </div>

        <div className="mb-6 relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, marca o SKU..."
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="admin-products-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((p) => (
              <ProductRow key={p.id} product={p} onSaved={fetch} />
            ))}
            {products.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-[#E7E5E4]">
                <Package className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
                <p className="text-[#57534E]">No hay productos que coincidan</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
